// src/pages/AbonnementsCreate.js - VERSION FINALE CORRIGÉE
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import PaiementNitaAmanaModal from "../components/PaiementNitaAmanaModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Compte à rebours jusqu'à l'expiration (24h) d'un code d'achat NITA/Amana
// (même composant que sur AbonnementsDetails.js).
const CompteARebours = ({ createdAt }) => {
  const [texte, setTexte] = useState('');

  useEffect(() => {
    const calculer = () => {
      const limite = new Date(createdAt.replace(' ', 'T')).getTime() + 24 * 60 * 60 * 1000;
      const restantMs = limite - Date.now();
      if (restantMs <= 0) {
        setTexte('Référence expirée');
        return;
      }
      const heures = Math.floor(restantMs / (1000 * 60 * 60));
      const minutes = Math.floor((restantMs % (1000 * 60 * 60)) / (1000 * 60));
      setTexte(`Temps restant pour payer : ${heures}h ${minutes}min`);
    };
    calculer();
    const interval = setInterval(calculer, 60000);
    return () => clearInterval(interval);
  }, [createdAt]);

  return <span>{texte}</span>;
};

const AbonnementsCreate = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    id_societe: "",
    id_offre: "",
    numero_recu: "",
    mode_paiement: "manuel"
  });

  const [societes, setSocietes] = useState([]);
  const [offres, setOffres] = useState([]);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSocietes, setLoadingSocietes] = useState(true);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedSociete, setSelectedSociete] = useState(null);
  const [selectedOffre, setSelectedOffre] = useState(null);
  const [etape, setEtape] = useState(1);
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [paiementEnCours, setPaiementEnCours] = useState(null); // { id_paiement, montant, newAbonnementId }

  // 🔍 ÉTATS POUR LA VÉRIFICATION D'ABONNEMENT
  const [aAbonnementActif, setAAbonnementActif] = useState(false);
  const [abonnementActuel, setAbonnementActuel] = useState(null);
  const [paiementEnAttente, setPaiementEnAttente] = useState(null);
  const [annulationEnCours, setAnnulationEnCours] = useState(false);

  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    accent: '#4FC3F7',
    darkText: '#2E2E2E',
    lightBg: '#F5F7FA',
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentProfile(String(user.profile));
        setCurrentUser(user);
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }
  }, []);

  useEffect(() => {
    if (currentProfile) {
      fetchSocietes();
      fetchOffres();
    }
  }, [currentProfile]);

  // 🔍 FONCTION DE VÉRIFICATION D'ABONNEMENT
  const verifierAbonnementSociete = useCallback(async (societeId) => {
    if (!societeId) return;
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${API_BASE_URL}/abonnements/verifier_statut.php?societe_id=${societeId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data?.success) {
        setAAbonnementActif(response.data.abonnement_actif);
        setAbonnementActuel(response.data.details || null);
        setPaiementEnAttente(response.data.paiement_en_attente || null);
      }
    } catch (error) {
      console.error("Erreur vérification abonnement:", error);
    }
  }, []);

  // Reprend le paiement en attente (rouvre la modal avec le même id_paiement)
  const handleFinaliserPaiementEnAttente = () => {
    if (!paiementEnAttente) return;
    setPaiementEnCours({
      id_paiement: paiementEnAttente.id_paiement,
      montant: paiementEnAttente.montant,
      newAbonnementId: paiementEnAttente.id_abonnement,
    });
    setShowPaiementModal(true);
  };

  // Annule le paiement en attente pour libérer la société (nouvel essai possible)
  const handleAnnulerPaiementEnAttente = async () => {
    if (!paiementEnAttente) return;
    if (!window.confirm("Annuler ce paiement en attente ? Vous pourrez ensuite souscrire un nouvel abonnement.")) {
      return;
    }
    setAnnulationEnCours(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/abonnements/annuler_paiement.php`,
        { id_paiement: paiementEnAttente.id_paiement },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setPaiementEnAttente(null);
      if (currentUser?.societe_id) {
        verifierAbonnementSociete(currentProfile === "1" ? form.id_societe : currentUser.societe_id);
      }
    } catch (err) {
      setServerError(err.response?.data?.message || "Impossible d'annuler ce paiement.");
    } finally {
      setAnnulationEnCours(false);
    }
  };

  // Pour le gestionnaire, vérifier automatiquement au chargement
  useEffect(() => {
    if (currentProfile === "3" && currentUser?.societe_id) {
      verifierAbonnementSociete(currentUser.societe_id);
    }
  }, [currentProfile, currentUser, verifierAbonnementSociete]);

  const fetchSocietes = async () => {
    setLoadingSocietes(true);
    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/societes/list.php`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        const societesData = response.data.societes || [];
        setSocietes(societesData);
        
        if (currentProfile === "3" && currentUser?.societe_id) {
          const societe = societesData.find(s => s.id_societe == currentUser.societe_id);
          if (societe) {
            setSelectedSociete(societe);
            setForm(prev => ({ ...prev, id_societe: societe.id_societe.toString() }));
          }
        }
      }
    } catch (error) {
      console.error("Erreur fetchSocietes:", error);
    } finally {
      setLoadingSocietes(false);
    }
  };

  const fetchOffres = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/offres/list.php`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        const offresData = response.data.offres || [];
        setOffres(offresData);
      }
    } catch (error) {
      console.error("Erreur chargement offres:", error);
    }
  };

  const formatPrix = (montant) => {
    const montantArrondi = Math.round(parseFloat(montant) || 0);
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montantArrondi);
  };

  const formatDuree = (mois) => {
    if (mois === 1) return "1 mois";
    if (mois < 12) return `${mois} mois`;
    const annees = mois / 12;
    return annees === 1 ? "1 an" : `${annees} ans`;
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'annuel': return 'bi-calendar-year';
      case 'mensuel': return 'bi-calendar-month';
      case 'trimestriel': return 'bi-calendar3';
      case 'semestriel': return 'bi-calendar2-range';
      default: return 'bi-calendar';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'annuel': return '#0B3C5D';
      case 'mensuel': return '#1E88C8';
      case 'trimestriel': return '#4FC3F7';
      case 'semestriel': return '#2196f3';
      default: return '#6c757d';
    }
  };

  const calculerPrixMensuel = (montant, dureeMois) => {
    if (dureeMois === 0) return 0;
    return montant / dureeMois;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }

    if (name === "id_societe") {
      const societe = societes.find(s => s.id_societe == value);
      setSelectedSociete(societe);
      // 🔍 Vérifier l'abonnement quand on change de société
      if (value) {
        verifierAbonnementSociete(value);
      } else {
        setAAbonnementActif(false);
        setAbonnementActuel(null);
      }
    }

    if (name === "id_offre") {
      const offre = offres.find(o => o.id_offre == value);
      setSelectedOffre(offre);
    }

    if (name === "mode_paiement") {
      setForm(prev => ({ 
        ...prev, 
        mode_paiement: value,
        numero_recu: value === 'manuel' ? prev.numero_recu : ''
      }));
    }
  };

  const selectOffre = (offre) => {
    setForm(prev => ({ ...prev, id_offre: offre.id_offre }));
    setSelectedOffre(offre);
    if (errors.id_offre) setErrors(prev => ({ ...prev, id_offre: "" }));
  };

  const validateEtape = (etapeNum) => {
    const e = {};
    
    if (etapeNum === 1) {
      if (currentProfile === "1" && !form.id_societe) {
        e.id_societe = "La société est requise.";
      }
    }
    
    if (etapeNum === 2) {
      if (!form.id_offre) {
        e.id_offre = "Veuillez sélectionner une offre.";
      }
    }
    
    if (etapeNum === 3) {
      if (currentProfile === "1") {
        if (!form.mode_paiement) {
          e.mode_paiement = "Le mode de paiement est requis.";
        }
        if (form.mode_paiement === "manuel" && !form.numero_recu?.trim()) {
          e.numero_recu = "Le numéro de reçu est requis pour les paiements manuels.";
        }
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextEtape = () => {
    // Un essai actif ne bloque pas la souscription : c'est justement le
    // moyen de sortir de l'essai. Seul un abonnement déjà payant et actif bloque.
    if (aAbonnementActif && abonnementActuel?.type_offre !== 'essai') {
      setServerError(
        `❌ Cette société a déjà un abonnement actif (${abonnementActuel?.offre}) jusqu'au ${
          new Date(abonnementActuel?.date_fin).toLocaleDateString('fr-FR')
        }. Vous ne pouvez pas souscrire un nouvel abonnement.`
      );
      return;
    }

    // Un paiement en ligne déjà en attente (non expiré) bloque toute nouvelle
    // souscription : il faut d'abord le finaliser ou l'annuler.
    if (paiementEnAttente) {
      setServerError("❌ Un paiement est déjà en attente pour cette société. Finalisez-le ou annulez-le ci-dessus avant d'en créer un nouveau.");
      return;
    }

    if (validateEtape(etape)) {
      setEtape(prev => Math.min(prev + 1, 3));
    }
  };

  const prevEtape = () => {
    setEtape(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateEtape(3)) {
      return;
    }

    // Vérification finale
    if (aAbonnementActif && abonnementActuel?.type_offre !== 'essai') {
      setServerError("❌ Impossible de créer l'abonnement : cette société a déjà un abonnement actif.");
      return;
    }
    if (paiementEnAttente) {
      setServerError("❌ Un paiement est déjà en attente pour cette société. Finalisez-le ou annulez-le avant d'en créer un nouveau.");
      return;
    }
    
    setLoading(true);
    setServerError("");
    setSuccessMsg("");

    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/abonnements/create.php`;
      
      const dataToSend = {
        id_societe: parseInt(form.id_societe),
        id_offre: parseInt(form.id_offre),
        numero_recu: form.numero_recu?.trim() || "",
        mode_paiement: currentProfile === "1" ? form.mode_paiement : "en_ligne"
      };

      const response = await axios.post(url, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      if (data.success) {
        if (data.abonnement?.statut_paiement === 'pending') {
          // Gestionnaire (mode_paiement 'en_ligne') : la souscription n'est
          // qu'en attente tant que le paiement NITA/AMANA n'est pas réellement
          // confirmé — on ouvre la modal de paiement au lieu d'annoncer un
          // succès prématuré (avant ce correctif, aucun paiement n'était
          // jamais collecté et l'abonnement restait pourtant présenté comme créé).
          setPaiementEnCours({
            id_paiement: data.id_paiement,
            montant: selectedOffre?.montant,
            newAbonnementId: data.abonnement?.id_abonnement,
          });
          setShowPaiementModal(true);
          setLoading(false);
          return;
        }

        let message = `✅ Abonnement créé avec succès.

📋 Référence: ${data.abonnement?.id_abonnement}
🏢 Société: ${selectedSociete?.nom_societe}
📦 Offre: ${selectedOffre?.libele_offre}
💰 Montant: ${formatPrix(selectedOffre?.montant)}
📅 Durée: ${formatDuree(selectedOffre?.duree_mois)}`;

        if (currentProfile === "1" && form.mode_paiement === "manuel") {
          message += `\n\n📝 Numéro de reçu: ${form.numero_recu}`;
        }

        setSuccessMsg(message);

        // ✅ REDIRECTION DIRECTE POUR TOUS LES PROFILS
        setTimeout(() => {
          navigate("/abonnements");
        }, 3000);
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response) {
        const { status, data } = err.response;
        
        if (status === 403 && data.code === 'ABONNEMENT_EXISTANT') {
          setServerError(data.message);
          setAAbonnementActif(true);
          setAbonnementActuel(data.abonnement_actuel);
        } else if (status === 401) {
          setServerError("Session expirée.");
          setTimeout(() => navigate("/login"), 2000);
        } else {
          setServerError(data?.message || "Erreur lors de la création.");
        }
      } else {
        setServerError("Erreur technique.");
      }
    } finally {
      setLoading(false);
    }
  };


  if (currentProfile !== "1" && currentProfile !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Nouvel abonnement</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                  Dashboard
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/abonnements" onClick={(e) => { e.preventDefault(); navigate("/abonnements"); }}>
                  Abonnements
                </a>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Nouveau</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#ffebee',
                  color: COLORS.danger
                }}>
                  <i className="bi bi-shield-lock fs-1"></i>
                </div>
              </div>
              <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
              <p className="text-muted mb-4" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                Cette fonctionnalité est réservée aux administrateurs et gestionnaires.
              </p>
              
              <button
                className="btn fw-semibold mt-3"
                onClick={() => navigate("/abonnements")}
                style={{
                  color: COLORS.primary,
                  border: `2px solid ${COLORS.primary}`,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = COLORS.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour aux abonnements
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const Stepper = () => (
    <div className="stepper mb-5">
      <div className="d-flex justify-content-between position-relative">
        <div className="stepper-line" style={{ 
          position: 'absolute',
          top: '12px',
          left: '50px',
          right: '50px',
          height: '2px',
          backgroundColor: '#e0e0e0',
          zIndex: 1
        }}></div>
        
        {[1, 2, 3].map((step) => (
          <div key={step} className="d-flex flex-column align-items-center" style={{ position: 'relative', zIndex: 2 }}>
            <div className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${etape >= step ? 'active' : ''}`}
              style={{ 
                width: '24px',
                height: '24px',
                backgroundColor: etape >= step ? COLORS.primary : '#e0e0e0',
                color: etape >= step ? COLORS.white : COLORS.darkText,
                border: `2px solid ${etape >= step ? COLORS.primary : '#e0e0e0'}`,
                fontSize: '0.9rem',
                fontWeight: '600'
              }}>
              {step}
            </div>
            <span style={{ 
              fontSize: '0.85rem',
              fontWeight: etape >= step ? '600' : '400',
              color: etape >= step ? COLORS.primary : COLORS.darkText
            }}>
              {step === 1 ? 'Société' : step === 2 ? 'Offre' : 'Paiement'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>
          <i className="bi bi-calendar-check me-2"></i>
          Nouvel abonnement
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                Dashboard
              </a>
            </li>
            <li className="breadcrumb-item">
              <a href="/abonnements" onClick={(e) => { e.preventDefault(); navigate("/abonnements"); }}>
                Abonnements
              </a>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
              {currentProfile === "1" ? "Créer un abonnement" : "Souscrire à un abonnement"}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderTop: `4px solid ${COLORS.primary}`
            }}>
              <div className="card-body p-4">
                <div className="text-center mb-5">
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ 
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#E3F2FD',
                    color: COLORS.primary
                  }}>
                    <i className={`bi ${etape === 1 ? 'bi-building' : etape === 2 ? 'bi-box' : 'bi-credit-card'} fs-3`}></i>
                  </div>
                  <h3 style={{ color: COLORS.primary, fontWeight: '600', marginBottom: '8px' }}>
                    {currentProfile === "1" ? "Créer un abonnement" : "Souscrire à un abonnement"}
                  </h3>
                  <p className="text-muted" style={{ color: COLORS.darkText }}>
                    {currentProfile === "1" 
                      ? "Créez un nouvel abonnement pour une société avec paiement manuel"
                      : "Souscrivez à un abonnement pour votre société via paiement sécurisé"}
                  </p>
                </div>

                <Stepper />

                {serverError && (
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{serverError}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setServerError("")}
                      ></button>
                    </div>
                  </div>
                )}
                
                {successMsg && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`,
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                      <div className="flex-grow-1">
                        {successMsg}
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSuccessMsg("")}
                      ></button>
                    </div>
                  </div>
                )}

                {/* ÉTAPE 1: Sélection de la société */}
                {etape === 1 && (
                  <div className="etape-content">
                    <div className="row">
                      <div className="col-md-8 mx-auto">
                        <div className="card" style={{ 
                          border: `1px solid ${COLORS.lightBg}`,
                          borderRadius: '8px',
                          backgroundColor: COLORS.lightBg
                        }}>
                          <div className="card-body p-4">
                            <h5 className="mb-4" style={{ color: COLORS.primary, fontWeight: '600' }}>
                              <i className="bi bi-building me-2"></i>
                              {currentProfile === "1" ? "Sélectionnez une société" : "Votre société"}
                            </h5>
                            
                            {currentProfile === "3" ? (
                              loadingSocietes ? (
                                <div className="text-center py-4">
                                  <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                                    <span className="visually-hidden">Chargement...</span>
                                  </div>
                                  <p className="mt-2" style={{ color: COLORS.darkText }}>
                                    Chargement de votre société...
                                  </p>
                                </div>
                              ) : selectedSociete ? (
                                <>
                                  <div className="alert alert-info" style={{ backgroundColor: '#E3F2FD', borderColor: COLORS.accent }}>
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-info-circle me-2" style={{ color: COLORS.primary }}></i>
                                      <div>
                                        <strong>Vous souscrivez pour votre propre société</strong>
                                        <div className="text-muted mt-1">
                                          {selectedSociete.nom_societe} - {selectedSociete.email}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* 🔍 MESSAGE SI ABONNEMENT ACTIF (pour les deux profils) */}
                                  {aAbonnementActif && abonnementActuel?.type_offre === 'essai' && (
                                    <div className="alert alert-info mt-4" style={{
                                      backgroundColor: '#E3F2FD',
                                      borderLeft: `4px solid ${COLORS.accent}`,
                                      borderRadius: '8px'
                                    }}>
                                      <div className="d-flex">
                                        <div className="me-3">
                                          <i className="bi bi-stars fs-4" style={{ color: COLORS.primary }}></i>
                                        </div>
                                        <div>
                                          <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                                            Période d'essai en cours
                                          </h6>
                                          <p className="mb-0" style={{ color: COLORS.darkText }}>
                                            <strong>{abonnementActuel?.offre}</strong> - Valable jusqu'au{' '}
                                            <strong>{new Date(abonnementActuel?.date_fin).toLocaleDateString('fr-FR')}</strong>.
                                            Choisissez une offre payante ci-dessous pour continuer au-delà de l'essai.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {aAbonnementActif && abonnementActuel?.type_offre !== 'essai' && (
                                    <div className="alert alert-warning mt-4" style={{
                                      backgroundColor: '#FFF3E0',
                                      borderLeft: `4px solid ${COLORS.warning}`,
                                      borderRadius: '8px'
                                    }}>
                                      <div className="d-flex">
                                        <div className="me-3">
                                          <i className="bi bi-exclamation-triangle fs-4" style={{ color: COLORS.warning }}></i>
                                        </div>
                                        <div>
                                          <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                                            Cette société a déjà un abonnement actif
                                          </h6>
                                          <p className="mb-2" style={{ color: COLORS.darkText }}>
                                            <strong>{abonnementActuel?.offre}</strong> - Valable jusqu'au{' '}
                                            <strong>{new Date(abonnementActuel?.date_fin).toLocaleDateString('fr-FR')}</strong>
                                          </p>
                                          <p className="mb-0 small" style={{ color: COLORS.darkText }}>
                                            <i className="bi bi-info-circle me-1"></i>
                                            Vous ne pouvez pas souscrire un nouvel abonnement, mais vous pouvez{' '}
                                            <strong>faire des ajouts</strong> sur les :
                                            <span className="badge bg-info ms-2 me-1">Tags (QR code/NFC)</span>
                                            <span className="badge bg-info me-1">Clients</span>
                                            <span className="badge bg-info">Sites</span>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Paiement myNITA/Amana en attente (non expiré) : il faut le finaliser
                                      ou l'annuler avant de pouvoir souscrire un nouvel abonnement. */}
                                  {paiementEnAttente && (
                                    <div className="alert mt-4" style={{
                                      backgroundColor: '#FFF3E0',
                                      borderLeft: `4px solid ${COLORS.warning}`,
                                      borderRadius: '8px'
                                    }}>
                                      <div className="d-flex">
                                        <div className="me-3">
                                          <i className="bi bi-hourglass-split fs-4" style={{ color: COLORS.warning }}></i>
                                        </div>
                                        <div className="flex-grow-1">
                                          <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                                            {paiementEnAttente.expire ? 'Paiement expiré (24h)' : 'Paiement en attente'}
                                          </h6>
                                          <p className="mb-2" style={{ color: COLORS.darkText }}>
                                            <strong>{paiementEnAttente.libele}</strong> — {new Intl.NumberFormat('fr-FR').format(paiementEnAttente.montant)} FCFA
                                            <br />
                                            {paiementEnAttente.gateway ? (
                                              <span className="small">
                                                Référence : <span style={{ fontFamily: 'monospace' }}>{paiementEnAttente.code_achat || paiementEnAttente.reference}</span>
                                                {' '}({paiementEnAttente.gateway === 'nita' ? 'NITA' : 'Amana'})
                                                {paiementEnAttente.expire
                                                  ? ' — cette référence a expiré, générez-en une nouvelle.'
                                                  : ' — valable 24h.'}
                                              </span>
                                            ) : (
                                              <span className="small">
                                                Aucun moyen de paiement n'a encore été choisi pour cet abonnement.
                                              </span>
                                            )}
                                          </p>
                                          {!paiementEnAttente.expire && paiementEnAttente.gateway && (
                                            <p className="small mb-2" style={{ color: COLORS.darkText }}>
                                              <i className="bi bi-clock-history me-1"></i>
                                              <CompteARebours createdAt={paiementEnAttente.created_at} />
                                            </p>
                                          )}
                                          <div className="d-flex gap-2">
                                            {(paiementEnAttente.expire || !paiementEnAttente.gateway) && (
                                              <button
                                                type="button"
                                                className="btn btn-sm fw-semibold"
                                                onClick={handleFinaliserPaiementEnAttente}
                                                style={{ backgroundColor: COLORS.primary, color: 'white', borderRadius: '6px' }}
                                              >
                                                <i className="bi bi-arrow-clockwise me-1"></i>
                                                {paiementEnAttente.gateway ? 'Générer une nouvelle référence' : 'Choisir un moyen de paiement'}
                                              </button>
                                            )}
                                            <button
                                              type="button"
                                              className="btn btn-sm fw-semibold"
                                              onClick={handleAnnulerPaiementEnAttente}
                                              disabled={annulationEnCours}
                                              style={{ border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: '6px', backgroundColor: 'white' }}
                                            >
                                              <i className="bi bi-x-circle me-1"></i> {annulationEnCours ? 'Annulation...' : 'Annuler'}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="alert alert-warning">
                                  <i className="bi bi-exclamation-triangle me-2"></i>
                                  Votre société n'a pas pu être chargée.
                                </div>
                              )
                            ) : (
                              // ADMIN
                              <>
                                <label className="form-label fw-semibold mb-3" style={{ color: COLORS.darkText }}>
                                  <i className="bi bi-buildings me-1" style={{ color: COLORS.secondary }}></i>
                                  Société <span className="text-danger">*</span>
                                </label>
                                
                                <div className="input-group mb-3">
                                  <span className="input-group-text" style={{ 
                                    backgroundColor: COLORS.white,
                                    borderColor: errors.id_societe ? COLORS.danger : '#e0e0e0',
                                    color: COLORS.secondary
                                  }}>
                                    <i className="bi bi-search"></i>
                                  </span>
                                  <select
                                    name="id_societe"
                                    className={`form-control ${errors.id_societe ? "is-invalid" : ""}`}
                                    value={form.id_societe}
                                    onChange={handleChange}
                                    disabled={loadingSocietes}
                                    style={{ 
                                      borderColor: errors.id_societe ? COLORS.danger : '#e0e0e0',
                                      color: COLORS.darkText
                                    }}
                                  >
                                    <option value="">Sélectionner une société...</option>
                                    {societes.map(societe => (
                                      <option key={societe.id_societe} value={societe.id_societe}>
                                        {societe.nom_societe} - {societe.email}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                
                                {errors.id_societe && (
                                  <div className="text-danger small mt-2">{errors.id_societe}</div>
                                )}

                                {/* 🔍 MESSAGE SI ABONNEMENT ACTIF POUR ADMIN */}
                                {aAbonnementActif && selectedSociete && abonnementActuel?.type_offre === 'essai' && (
                                  <div className="alert alert-info mt-4" style={{
                                    backgroundColor: '#E3F2FD',
                                    borderLeft: `4px solid ${COLORS.accent}`,
                                    borderRadius: '8px'
                                  }}>
                                    <div className="d-flex">
                                      <div className="me-3">
                                        <i className="bi bi-stars fs-4" style={{ color: COLORS.primary }}></i>
                                      </div>
                                      <div>
                                        <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                                          Cette société est en période d'essai
                                        </h6>
                                        <p className="mb-0" style={{ color: COLORS.darkText }}>
                                          <strong>{abonnementActuel?.offre}</strong> - Valable jusqu'au{' '}
                                          <strong>{new Date(abonnementActuel?.date_fin).toLocaleDateString('fr-FR')}</strong>.
                                          Vous pouvez lui souscrire une offre payante ci-dessous.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {aAbonnementActif && selectedSociete && abonnementActuel?.type_offre !== 'essai' && (
                                  <div className="alert alert-warning mt-4" style={{
                                    backgroundColor: '#FFF3E0',
                                    borderLeft: `4px solid ${COLORS.warning}`,
                                    borderRadius: '8px'
                                  }}>
                                    <div className="d-flex">
                                      <div className="me-3">
                                        <i className="bi bi-exclamation-triangle fs-4" style={{ color: COLORS.warning }}></i>
                                      </div>
                                      <div>
                                        <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                                          Attention : Cette société a déjà un abonnement actif
                                        </h6>
                                        <p className="mb-2" style={{ color: COLORS.darkText }}>
                                          <strong>{abonnementActuel?.offre}</strong> - Valable jusqu'au{' '}
                                          <strong>{new Date(abonnementActuel?.date_fin).toLocaleDateString('fr-FR')}</strong>
                                        </p>
                                        <p className="mb-0 small" style={{ color: COLORS.darkText }}>
                                          <i className="bi bi-info-circle me-1"></i>
                                          Cette société ne peut pas souscrire un nouvel abonnement, mais peut faire des ajouts :
                                          <span className="badge bg-info ms-2 me-1">Tags (QR code/NFC)</span>
                                          <span className="badge bg-info me-1">Clients</span>
                                          <span className="badge bg-info">Sites</span>
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {selectedSociete && paiementEnAttente && (
                                  <div className="alert mt-4" style={{
                                    backgroundColor: '#FFF3E0',
                                    borderLeft: `4px solid ${COLORS.warning}`,
                                    borderRadius: '8px'
                                  }}>
                                    <div className="d-flex">
                                      <div className="me-3">
                                        <i className="bi bi-hourglass-split fs-4" style={{ color: COLORS.warning }}></i>
                                      </div>
                                      <div className="flex-grow-1">
                                        <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                                          {paiementEnAttente.expire ? 'Paiement expiré (24h)' : 'Paiement en attente'}
                                        </h6>
                                        <p className="mb-2" style={{ color: COLORS.darkText }}>
                                          <strong>{paiementEnAttente.libele}</strong> — {new Intl.NumberFormat('fr-FR').format(paiementEnAttente.montant)} FCFA
                                          <br />
                                          {paiementEnAttente.gateway ? (
                                            <span className="small">
                                              Référence : <span style={{ fontFamily: 'monospace' }}>{paiementEnAttente.code_achat || paiementEnAttente.reference}</span>
                                              {' '}({paiementEnAttente.gateway === 'nita' ? 'NITA' : 'Amana'})
                                              {paiementEnAttente.expire
                                                ? ' — cette référence a expiré, générez-en une nouvelle.'
                                                : ' — valable 24h.'}
                                            </span>
                                          ) : (
                                            <span className="small">
                                              Aucun moyen de paiement n'a encore été choisi pour cet abonnement.
                                            </span>
                                          )}
                                        </p>
                                        <div className="d-flex gap-2">
                                          {(paiementEnAttente.expire || !paiementEnAttente.gateway) && (
                                            <button
                                              type="button"
                                              className="btn btn-sm fw-semibold"
                                              onClick={handleFinaliserPaiementEnAttente}
                                              style={{ backgroundColor: COLORS.primary, color: 'white', borderRadius: '6px' }}
                                            >
                                              <i className="bi bi-arrow-clockwise me-1"></i>
                                              {paiementEnAttente.gateway ? 'Générer une nouvelle référence' : 'Choisir un moyen de paiement'}
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            className="btn btn-sm fw-semibold"
                                            onClick={handleAnnulerPaiementEnAttente}
                                            disabled={annulationEnCours}
                                            style={{ border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: '6px', backgroundColor: 'white' }}
                                          >
                                            <i className="bi bi-x-circle me-1"></i> {annulationEnCours ? 'Annulation...' : 'Annuler'}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ÉTAPE 2: Sélection de l'offre (STYLE IDENTIQUE) */}
                {etape === 2 && (
                  <div className="etape-content">
                    <div className="row">
                      <div className="col-12">
                        <h5 className="mb-4" style={{ color: COLORS.primary, fontWeight: '600' }}>
                          <i className="bi bi-box me-2"></i>
                          Choisissez une offre d'abonnement
                        </h5>
                        
                        {errors.id_offre && (
                          <div className="alert alert-danger mb-4">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {errors.id_offre}
                          </div>
                        )}

                        <div className="row">
                          {offres
                            // L'essai est attribué automatiquement à la validation d'une société,
                            // jamais choisi manuellement ici — on ne propose que les offres payantes.
                            .filter(offre => offre.statut_offre === 1 && offre.supprimer === 0 && offre.type_offre !== 'essai')
                            .map(offre => {
                              const prixMensuel = calculerPrixMensuel(offre.montant, offre.duree_mois);
                              const typeColor = getTypeColor(offre.type_offre);
                              const isSelected = form.id_offre == offre.id_offre;
                              
                              return (
                                <div key={offre.id_offre} className="col-md-6 mb-4">
                                  <div 
                                    className="card h-100 cursor-pointer"
                                    onClick={() => selectOffre(offre)}
                                    style={{ 
                                      borderRadius: '12px',
                                      overflow: 'hidden',
                                      transition: 'all 0.3s ease',
                                      border: `2px solid ${isSelected ? COLORS.primary : '#e0e0e0'}`,
                                      backgroundColor: isSelected ? '#E3F2FD' : COLORS.white,
                                      cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected) {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                                        e.currentTarget.style.borderColor = COLORS.accent;
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSelected) {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                      }
                                    }}
                                  >
                                    <div className="card-header border-0 pb-0" style={{ 
                                      backgroundColor: isSelected ? COLORS.primary : typeColor,
                                      color: COLORS.white,
                                      padding: '20px'
                                    }}>
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                          <h5 className="card-title mb-1" style={{ 
                                            color: COLORS.white,
                                            fontSize: '1.1rem',
                                            fontWeight: '600'
                                          }}>
                                            {offre.libele_offre}
                                          </h5>
                                          <div className="d-flex align-items-center">
                                            <i className={`bi ${getTypeIcon(offre.type_offre)} me-2`}></i>
                                            <span className="badge fw-semibold" style={{ 
                                              backgroundColor: 'rgba(255,255,255,0.2)',
                                              color: COLORS.white,
                                              fontSize: '0.75rem',
                                              padding: '4px 8px',
                                              borderRadius: '4px',
                                              textTransform: 'uppercase'
                                            }}>
                                              {offre.type_offre}
                                            </span>
                                          </div>
                                        </div>
                                        
                                        {isSelected && (
                                          <span className="badge fw-semibold" style={{ 
                                            backgroundColor: COLORS.success,
                                            color: COLORS.white,
                                            fontSize: '0.7rem',
                                            padding: '4px 8px',
                                            borderRadius: '4px'
                                          }}>
                                            <i className="bi bi-check-circle me-1"></i>
                                            Sélectionnée
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="card-body pt-4">
                                      <div className="mb-4">
                                        <p className="card-text" style={{ 
                                          color: COLORS.darkText,
                                          fontSize: '0.9rem',
                                          minHeight: '60px'
                                        }}>
                                          {offre.description || "Aucune description disponible."}
                                        </p>
                                      </div>
                                      
                                      <div className="row mb-4">
                                        <div className="col-6">
                                          <div className="text-center p-3 rounded" style={{ 
                                            backgroundColor: '#E3F2FD',
                                            border: `1px solid ${COLORS.secondary}20`
                                          }}>
                                            <div className="mb-1">
                                              <i className="bi bi-calendar-check" style={{ 
                                                color: COLORS.secondary,
                                                fontSize: '1.2rem'
                                              }}></i>
                                            </div>
                                            <div className="fw-bold" style={{ 
                                              color: COLORS.primary,
                                              fontSize: '1.1rem'
                                            }}>
                                              {formatDuree(offre.duree_mois)}
                                            </div>
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                              Durée
                                            </small>
                                          </div>
                                        </div>
                                        
                                        <div className="col-6">
                                          <div className="text-center p-3 rounded" style={{ 
                                            backgroundColor: '#E8F5E9',
                                            border: `1px solid ${COLORS.success}20`
                                          }}>
                                            <div className="mb-1">
                                              <i className="bi bi-cash" style={{ 
                                                color: COLORS.success,
                                                fontSize: '1.2rem'
                                              }}></i>
                                            </div>
                                            <div className="fw-bold" style={{ 
                                              color: '#2e7d32',
                                              fontSize: '1.1rem'
                                            }}>
                                              {formatPrix(prixMensuel)}
                                            </div>
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                              Par mois
                                            </small>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <span style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                                            <i className="bi bi-cash-stack me-1"></i>
                                            Prix total
                                          </span>
                                          <span className="fw-bold" style={{ 
                                            color: COLORS.primary,
                                            fontSize: '1.3rem'
                                          }}>
                                            {formatPrix(offre.montant)}
                                          </span>
                                        </div>
                                        
                                        <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
                                          <div 
                                            className="progress-bar" 
                                            role="progressbar" 
                                            style={{ 
                                              width: '100%',
                                              backgroundColor: typeColor
                                            }}
                                            aria-valuenow="100" 
                                            aria-valuemin="0" 
                                            aria-valuemax="100"
                                          ></div>
                                        </div>
                                      </div>
                                      
                                      <div className="text-center mb-3">
                                        {isSelected ? (
                                          <span className="badge bg-success p-2">
                                            <i className="bi bi-check-circle me-1"></i>
                                            Offre sélectionnée
                                          </span>
                                        ) : (
                                          <span className="badge bg-secondary p-2">
                                            <i className="bi bi-arrow-right-circle me-1"></i>
                                            Cliquer pour sélectionner
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="card-footer border-0 pt-0 pb-3">
                                      <div className="d-flex justify-content-center">
                                        <button
                                          className="btn fw-semibold"
                                          onClick={() => selectOffre(offre)}
                                          style={{
                                            color: isSelected ? COLORS.white : COLORS.primary,
                                            backgroundColor: isSelected ? COLORS.success : 'transparent',
                                            borderColor: isSelected ? COLORS.success : COLORS.primary,
                                            borderRadius: '8px',
                                            padding: '8px 24px',
                                            transition: 'all 0.3s ease',
                                            flex: 1
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!isSelected) {
                                              e.target.style.backgroundColor = COLORS.primary;
                                              e.target.style.color = COLORS.white;
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!isSelected) {
                                              e.target.style.backgroundColor = 'transparent';
                                              e.target.style.color = COLORS.primary;
                                            }
                                          }}
                                        >
                                          <i className={`bi ${isSelected ? 'bi-check-lg' : 'bi-cursor'} me-2`}></i>
                                          {isSelected ? 'Sélectionnée ✓' : 'Sélectionner cette offre'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>

                        {/* Message pour guider vers les ajouts si abonnement actif (pas pendant l'essai) */}
                        {aAbonnementActif && abonnementActuel?.type_offre !== 'essai' && (
                          <div className="alert alert-info text-center mt-4" style={{ backgroundColor: '#E3F2FD' }}>
                            <i className="bi bi-info-circle me-2"></i>
                            Pour ajouter des <strong>tags, clients ou sites</strong> à votre abonnement actuel, 
                            <a href="/achat-quota" className="fw-semibold ms-1" style={{ color: COLORS.primary, textDecoration: 'underline' }}>
                              cliquez ici
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ÉTAPE 3: Paiement */}
                {etape === 3 && (
                  <div className="etape-content">
                    <div className="row">
                      <div className="col-md-8 mx-auto">
                        <div className="card" style={{ 
                          border: `1px solid ${COLORS.lightBg}`,
                          borderRadius: '8px'
                        }}>
                          <div className="card-body p-4">
                            <h5 className="mb-4" style={{ color: COLORS.primary, fontWeight: '600' }}>
                              <i className="bi bi-credit-card me-2"></i>
                              Mode de paiement
                            </h5>
                            
                            <div className="alert alert-info mb-4" style={{ backgroundColor: '#E3F2FD', borderColor: COLORS.accent }}>
                              <div className="d-flex align-items-center">
                                <i className="bi bi-receipt me-3 fs-4" style={{ color: COLORS.primary }}></i>
                                <div>
                                  <strong>Récapitulatif</strong>
                                  <div className="row mt-2">
                                    <div className="col-md-6">
                                      <div className="small">Société</div>
                                      <div className="fw-semibold">{selectedSociete?.nom_societe}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="small">Offre</div>
                                      <div className="fw-semibold">{selectedOffre?.libele_offre}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {currentProfile === "1" && form.mode_paiement === 'manuel' && (
                              <div className="mb-4">
                                <label className="form-label fw-semibold mb-3" style={{ color: COLORS.darkText }}>
                                  <i className="bi bi-123 me-1" style={{ color: COLORS.secondary }}></i>
                                  Numéro de reçu <span className="text-danger">*</span>
                                </label>
                                
                                <div className="input-group">
                                  <span className="input-group-text" style={{ 
                                    backgroundColor: COLORS.white,
                                    borderColor: errors.numero_recu ? COLORS.danger : '#e0e0e0',
                                    color: COLORS.secondary
                                  }}>
                                    <i className="bi bi-receipt-cutoff"></i>
                                  </span>
                                  <input
                                    type="text"
                                    name="numero_recu"
                                    className={`form-control ${errors.numero_recu ? "is-invalid" : ""}`}
                                    value={form.numero_recu}
                                    onChange={handleChange}
                                    placeholder="Ex: RECU-2024-001234"
                                  />
                                </div>
                                {errors.numero_recu && (
                                  <div className="text-danger small mt-2">{errors.numero_recu}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 🔴 BOUTONS CORRIGÉS - C'EST ICI LE CHANGEMENT */}
                <div className="d-flex justify-content-between mt-5 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={etape === 1 ? () => navigate("/abonnements") : prevEtape}
                    disabled={loading}
                    style={{
                      color: COLORS.primary,
                      border: `2px solid ${COLORS.primary}`,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.color = COLORS.white;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    {etape === 1 ? 'Annuler' : 'Retour'}
                  </button>
                  
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={etape === 3 ? handleSubmit : nextEtape}
                    disabled={loading || (etape === 1 && ((aAbonnementActif && abonnementActuel?.type_offre !== 'essai') || !!paiementEnAttente))}
                    style={{
                      color: COLORS.white,
                      backgroundColor: (etape === 3 ? COLORS.success : COLORS.primary),
                      borderColor: (etape === 3 ? COLORS.success : COLORS.primary),
                      borderRadius: '8px',
                      padding: '10px 32px',
                      transition: 'all 0.3s ease',
                      opacity: (loading || (etape === 1 && ((aAbonnementActif && abonnementActuel?.type_offre !== 'essai') || !!paiementEnAttente))) ? 0.6 : 1,
                      cursor: (etape === 1 && ((aAbonnementActif && abonnementActuel?.type_offre !== 'essai') || !!paiementEnAttente)) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Traitement...
                      </>
                    ) : (
                      <>
                        {etape === 3 ? (
                          currentProfile === "1" ? (
                            <>
                              <i className="bi bi-check-circle me-2"></i>
                              Finaliser
                            </>
                          ) : (
                            <>
                              <i className="bi bi-credit-card me-2"></i>
                              Paiement
                            </>
                          )
                        ) : (
                          <>
                            Continuer
                            <i className="bi bi-arrow-right ms-2"></i>
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>

                {/* Lien rapide vers les ajouts si abonnement actif (indisponible pendant l'essai) */}
                {aAbonnementActif && etape === 1 && abonnementActuel?.type_offre !== 'essai' && (
                  <div className="text-center mt-4">
                    <a 
                      href="/achat-quota" 
                      className="btn fw-semibold"
                      style={{
                        color: COLORS.success,
                        border: `2px solid ${COLORS.success}`,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '8px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.success;
                        e.target.style.color = COLORS.white;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.success;
                      }}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Acheter des ajouts (Tags, Clients, Sites)
                    </a>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      <PaiementNitaAmanaModal
        show={showPaiementModal}
        idPaiement={paiementEnCours?.id_paiement}
        montant={paiementEnCours?.montant}
        onClose={() => {
          setShowPaiementModal(false);
          setServerError("Paiement mis de côté : il reste en attente (valable 24h). Vous pouvez le finaliser ou l'annuler ci-dessus.");
          const societeId = currentProfile === "1" ? form.id_societe : currentUser?.societe_id;
          if (societeId) verifierAbonnementSociete(societeId);
        }}
        onSuccess={() => {
          setShowPaiementModal(false);
          setSuccessMsg(`✅ Paiement confirmé, abonnement créé !\n\n📋 Référence: ${paiementEnCours?.newAbonnementId}\n\nRedirection vers la liste des abonnements...`);
          setTimeout(() => navigate("/abonnements"), 3000);
        }}
      />

      <style>{`
        .cursor-pointer { cursor: pointer; }
        .btn:disabled { cursor: not-allowed; }
        .modal.show { display: block; background-color: rgba(0,0,0,0.5); }
        
        .form-control:focus, .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
      `}</style>
    </Layout>
  );
};

export default AbonnementsCreate;